import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { ColumnTypeEnum, GbTableColumn, GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import * as UserAccountsActions from 'libs/gb-dashboard/staff/data-access-user-accounts/src/lib/+state/user-accounts.actions'
import { UserAccountsFacade } from 'libs/gb-dashboard/staff/data-access-user-accounts/src/lib/+state/user-accounts.facade'
import {
  UserAccountsEntity,
  userAccountsEntityName,
} from 'libs/gb-dashboard/staff/data-access-user-accounts/src/lib/+state/user-accounts.models'
import { map, Observable, of } from 'rxjs'
import { GbClientAccountsPreviewComponent } from '../gb-client-accounts-preview/gb-client-accounts-preview.component'
import { GbUserAccountsCampaignPreviewComponent } from '../gb-user-accounts-campaign-preview/gb-user-accounts-campaign-preview.component'
@Component({
  selector: 'gb-user-accounts',
  templateUrl: './gb-user-accounts.component.html',
  styleUrls: ['./gb-user-accounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GbUserAccountsComponent implements OnInit {
  searchTerm = ''
  ordering = ''
  userAccountsEntityName = userAccountsEntityName

  loaded$ = this.userAccountsFacade.loaded$
  dataView$ = of(GridSwitchEnum.Table)

  count$ = this.userAccountsFacade.count$
  searchHistory$ = this.userAccountsFacade.searchHistory$

  allUserAccountsWithActions$ = this.userAccountsFacade.allUserAccounts$.pipe(
    map((users: UserAccountsEntity[]) =>
      users.map((user) => ({
        ...user,
        email: user.is_active ? user.email : undefined,
        campaigns: {
          openDialog: () => this.openCampaignsPreview(user),
          items: [...user.campaigns.map((campaign) => campaign.url_name)],
          noItemsLabel: 'USER_ACCOUNTS.NO_CAMPAIGNS',
        },
        client_accounts: {
          openDialog: () => this.openOrgsPreview(user),
          items: [...user.client_accounts.map((account) => account.name)],
          noItemsLabel: 'USER_ACCOUNTS.NO_CLIENT_ACCOUNTS',
        },
        isProtected: !this.canBeArchived(user),
        actions: [
          {
            name: 'USER_ACCOUNTS.ARCHIVE',
            method: () => {
              this.userAccountsFacade.dispatch(UserAccountsActions.openArchiveUserDialog({ uuid: user.uuid }))
            },
            disabled: !this.canBeArchived(user),
          },
        ],
      })),
    ),
  )

  displayedColumns$: Observable<GbTableColumn[]> = of([
    {
      columnKey: 'first_name',
      columnLabel: 'USERS.FIRST_NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'last_name',
      columnLabel: 'USERS.LAST_NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'phone_number',
      columnLabel: 'USERS.PHONE_NUMBER',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'email',
      columnLabel: 'USERS.EMAIL',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'campaigns',
      columnLabel: 'USER_ACCOUNTS.CAMPAIGNS',
      columnType: ColumnTypeEnum.PreviewButton,
    },
    {
      columnKey: 'client_accounts',
      columnLabel: 'USER_ACCOUNTS.CLIENT_ACCOUNTS',
      columnType: ColumnTypeEnum.PreviewButton,
    },
    {
      columnKey: 'is_distributor',
      columnLabel: 'USER_ACCOUNTS.IS_DISTRIBUTOR',
      columnType: ColumnTypeEnum.Boolean,
    },
    {
      columnKey: 'is_staff',
      columnLabel: 'USER_ACCOUNTS.STAFF',
      columnType: ColumnTypeEnum.Boolean,
    },
    {
      columnKey: 'is_superuser',
      columnLabel: 'USER_ACCOUNTS.SUPERUSER',
      columnType: ColumnTypeEnum.Boolean,
    },
    {
      columnKey: 'mfa_enabled',
      columnLabel: 'USER_ACCOUNTS.MFA_ENABLED',
      columnType: ColumnTypeEnum.Boolean,
    },
    {
      columnKey: 'actions',
      columnLabel: 'ACTIONS',
      columnType: ColumnTypeEnum.Actions,
    },
  ])

  constructor(
    private userAccountsFacade: UserAccountsFacade,
    private gbDashboardDialogService: GbDashboardDialogService,
  ) {}

  openCampaignsPreview(user: UserAccountsEntity) {
    this.gbDashboardDialogService.openDialog(GbUserAccountsCampaignPreviewComponent, {
      width: '500px',
      data: {
        campaigns: user.campaigns,
      },
    })
  }

  openOrgsPreview(user: UserAccountsEntity) {
    this.gbDashboardDialogService.openDialog(GbClientAccountsPreviewComponent, {
      width: '500px',
      data: {
        clientAccounts: user.client_accounts,
      },
    })
  }

  ngOnInit(): void {
    this.loadUserAccounts({ shouldReset: true })
  }

  loadUserAccounts(options: { shouldReset: boolean } = { shouldReset: false }) {
    this.userAccountsFacade.dispatch(
      UserAccountsActions.loadUserAccounts({
        searchTerm: this.searchTerm,
        ordering: this.ordering,
        shouldReset: options.shouldReset,
      }),
    )
  }

  onToggle(event: GridSwitchEnum) {
    if (event === GridSwitchEnum.Grid) {
      this.userAccountsFacade.dispatch(UserAccountsActions.displayGrid())
    }
    if (event === GridSwitchEnum.Table) {
      this.userAccountsFacade.dispatch(UserAccountsActions.displayTable())
    }
  }

  searchUserAccounts(e: string) {
    this.searchTerm = e
    this.loadUserAccounts({ shouldReset: true })
  }

  sortUserAccounts(order: string) {
    if (order === this.ordering) {
      return
    }
    this.ordering = order
    this.loadUserAccounts({ shouldReset: true })
  }

  canBeArchived(user: UserAccountsEntity) {
    if (user.is_staff || user.is_superuser || user.is_distributor) return false // disallow archiving staff, superusers and distributors
    return user.is_active // allow archiving users not already archived
  }
}
